import * as Sentry from '@sentry/browser';
import { html, LitElement, type PropertyValues } from 'lit';
import { property, queryAssignedElements } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { useStepperStore } from '../../../../stores/stepper.store.js';
import styles from './select-card.styles.js';

/**
 * @cssproperty --y-selectcard-gap - Root gap
 *
 * @fires {CustomEvent} filter-change - Fired when a filter is changed
 */
export class SelectCardComponent extends LitElement {
  /** Private variables */
  @queryAssignedElements({ slot: 'filter' })
  private _filterElements!: HTMLLabelElement[];

  /** Protected variables */
  protected readonly filtersRef = createRef<HTMLElement>();

  /** Public variables */
  @property({ type: Boolean, reflect: true, attribute: 'hide-if-one' })
  public hideIfOne = false;

  @property({ type: Boolean, reflect: true, attribute: 'auto-register' })
  public autoRegister = false;

  /** constructor */
  protected override firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);

    if (this._filterElements.length === 0) {
      Sentry.captureMessage('Component [SelectCardComponent] requires at least one filter to be set.', 'error');
    }

    if (this._filterElements.length === 1 && this.hideIfOne) {
      this.filtersRef.value?.style.setProperty('display', 'none');
    }

    if (this.autoRegister) {
      useStepperStore().getState().extras.selectCard();
    }
  }

  override connectedCallback() {
    super.connectedCallback();

    this._filterElements.forEach((filter) => {
      filter.addEventListener('change', () => {
        this.dispatchEvent(
          new CustomEvent('filter-change', {
            bubbles: true,
            detail: { filter },
          }),
        );
      });
    });

    this.dispatchEvent(
      new CustomEvent('filter-change', {
        bubbles: true,
      }),
    );
  }

  /** Public methods */
  static override get styles() {
    return styles;
  }

  /** Protected methods */
  protected override render() {
    return html`
      <div class="filters" ${ref(this.filtersRef)}>
        <slot name="filter"></slot>
      </div>

      <section>
        <slot></slot>
      </section>

      <footer>
        <slot name="textfield"></slot>
      </footer>
    `;
  }

  /** Private methods */
}

export function defineSelectCardComponent() {
  if (!customElements.get('y-select-card')) {
    customElements.define('y-select-card', SelectCardComponent);
  }
}
